import { StorePermission } from "#navigation/types";

import { MembershipPermission } from "./types";

/**
 * Every legacy flag mapped to its vocabulary entry, or `null` where the flag was dropped.
 * Mirrors the server's `LEGACY_TO_STORE_PERMISSION`; the `Record` over the navigation enum
 * makes an unmapped flag a compile error, and the test asserts it at runtime as well.
 *
 * Two merges are deliberate and match the server guard. `hasEmployees` and `hasEditAdmin`
 * both mean "manage who is at this store" and become `team:write`. The seven report flags
 * were already OR'd into one gate on the dashboard and become `reports:read`; only
 * `hasFinancialReports` stays separate as `reports:financial`, because it gates money
 * movement. The four dropped flags gate nothing anywhere.
 */
export const LEGACY_TO_MEMBERSHIP_PERMISSION: Record<
    StorePermission,
    MembershipPermission | null
> = {
    [StorePermission.IsOwner]: "store:owner",
    [StorePermission.HasDashboard]: "dashboard:read",
    [StorePermission.HasOrders]: "orders:read",
    [StorePermission.HasMenuEditing]: "menu:write",
    [StorePermission.HasPromos]: "promos:write",
    [StorePermission.HasGuestbook]: "guestbook:read",
    [StorePermission.HasPayouts]: "payouts:read",
    [StorePermission.HasBilling]: "billing:read",
    [StorePermission.HasBillingWrite]: "billing:write",
    [StorePermission.HasSettings]: "settings:read",
    [StorePermission.HasSettingsWrite]: "settings:write",
    [StorePermission.HasCustomerFeeSettings]: "fees:write",
    [StorePermission.HasDevices]: "devices:write",

    [StorePermission.HasEmployees]: "team:write",
    [StorePermission.HasEditAdmin]: "team:write",

    [StorePermission.HasFinancialReports]: "reports:financial",

    [StorePermission.HasSalesReports]: "reports:read",
    [StorePermission.HasReportsMenu]: "reports:read",
    [StorePermission.HasReportsCustomers]: "reports:read",
    [StorePermission.HasReportsLocations]: "reports:read",
    [StorePermission.HasReportsPromotions]: "reports:read",
    [StorePermission.HasReportsGiftCards]: "reports:read",
    [StorePermission.HasReportsLabor]: "reports:read",

    [StorePermission.HasAnalytics]: null,
    [StorePermission.HasTopCustomers]: null,
    [StorePermission.HasPayroll]: null,
    [StorePermission.HasCatering]: null,
};

export const LEGACY_FLAGS = Object.values(StorePermission);

/** The legacy flags a vocabulary entry expands back into. Generated, never hand-written. */
export const MEMBERSHIP_PERMISSION_TO_LEGACY: Record<
    MembershipPermission,
    StorePermission[]
> = LEGACY_FLAGS.reduce(
    (acc, flag) => {
        const permission = LEGACY_TO_MEMBERSHIP_PERMISSION[flag];
        if (permission) {
            acc[permission] = [...(acc[permission] ?? []), flag];
        }
        return acc;
    },
    {} as Record<MembershipPermission, StorePermission[]>,
);
